import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { lazy, Suspense } from "react";
import "@/App.css";
import { AuthProvider, useAuth } from "@/lib/auth";
import { HotelProvider } from "@/lib/hotel";
import { UnreadProvider } from "@/lib/useUnread";
import { isGuestShellPath } from "@/lib/guestNav";
import { Toaster } from "@/components/ui/sonner";

// Eager — first-paint routes on the QR-arrival journey.
import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import ParticipantJoin from "@/pages/ParticipantJoin";

import Protected, { RouteFallback } from "@/components/Protected";
import ErrorBoundary from "@/components/ErrorBoundary";
import { PrivacyBanner } from "@/components/PrivacyBanner";
import { SessionIdleWarning } from "@/components/SessionIdleWarning";
import { InstallPrompt } from "@/components/InstallPrompt";
import { PropertyTheme } from "@/components/PropertyTheme";
import { NetworkStatus } from "@/components/NetworkStatus";
import { PushOptIn } from "@/components/PushOptIn";
import GuestNav from "@/components/GuestNav";
import DoctorNav from "@/components/DoctorNav";
import HotelNav from "@/components/HotelNav";
import AdminNav from "@/components/AdminNav";
import PharmacyNav from "@/components/PharmacyNav";
import LaboratoryNav from "@/components/LaboratoryNav";
import CourierNav from "@/components/CourierNav";
import CollectorNav from "@/components/CollectorNav";
import WorkspaceTopBar from "@/components/WorkspaceTopBar";

// Portal-scoped route groups — each file owns its lazy imports + guards.
import PatientRoutes    from "@/routes/PatientRoutes";
import DoctorRoutes     from "@/routes/DoctorRoutes";
import HotelRoutes      from "@/routes/HotelRoutes";
import AdminRoutes      from "@/routes/AdminRoutes";
import PharmacyRoutes   from "@/routes/PharmacyRoutes";
import LaboratoryRoutes from "@/routes/LaboratoryRoutes";

// Cross-portal / public utility routes that don't belong to any single portal.
const CourierDashboard    = lazy(() => import("@/pages/CourierDashboard"));
const CollectorDashboard  = lazy(() => import("@/pages/CollectorDashboard"));
const PartnerWorkspace    = lazy(() => import("@/pages/PartnerWorkspace"));
const ChainRollup         = lazy(() => import("@/pages/ChainRollup"));
const MfaSetup            = lazy(() => import("@/pages/MfaSetup"));
const Compliance          = lazy(() => import("@/pages/Compliance"));
const Privacy             = lazy(() => import("@/pages/Privacy"));
const Messages            = lazy(() => import("@/pages/Messages"));
const ConsentSettings     = lazy(() => import("@/pages/ConsentSettings"));
const PrivacySettings     = lazy(() => import("@/pages/PrivacySettings"));
const AuthCallback        = lazy(() => import("@/pages/AuthCallback"));
const HotelInviteAccept   = lazy(() => import("@/pages/HotelInviteAccept"));
const PartnerInviteAccept = lazy(() => import("@/pages/PartnerInviteAccept"));
const QrScan              = lazy(() => import("@/pages/QrScan"));
const StartWizard         = lazy(() => import("@/pages/StartWizard"));
const GuestLogin          = lazy(() => import("@/pages/GuestLogin"));
const Setup               = lazy(() => import("@/pages/Setup"));
const ResetCache          = lazy(() => import("@/pages/ResetCache"));
const VerifySignedDoc     = lazy(() => import("@/pages/VerifySignedDoc"));

/**
 * Global role-scoped navigation. Only renders on routes belonging to the
 * user's portal so we don't decorate the landing / QR / login flows.
 */
function RoleNav() {
  const { user, ready } = useAuth();
  const location = useLocation();
  const path = location.pathname;
  if (!ready || !user) return null;
  if (user.role === "patient" && isGuestShellPath(path)) return <GuestNav />;
  if (user.role === "doctor" && (path === "/doctor" || path.startsWith("/doctor/"))) {
    if (/^\/doctor\/consultation\//.test(path)) return null;
    return <><DoctorNav /><WorkspaceTopBar /></>;
  }
  if ((user.role === "hotel_staff" || user.role === "hotel_manager")
      && (path === "/hotel" || path.startsWith("/hotel/"))) return <><HotelNav /><WorkspaceTopBar /></>;
  if (user.role === "platform_admin"
      && (path === "/platform" || path.startsWith("/platform/"))) return <><AdminNav /><WorkspaceTopBar /></>;
  if (user.role === "pharmacy"
      && (path === "/pharmacy" || path.startsWith("/pharmacy/") || path.startsWith("/partner/"))) return <><PharmacyNav /><WorkspaceTopBar /></>;
  if (user.role === "laboratory"
      && (path === "/laboratory" || path.startsWith("/laboratory/") || path.startsWith("/partner/"))) return <><LaboratoryNav /><WorkspaceTopBar /></>;
  if (user.role === "courier" && (path === "/courier" || path.startsWith("/courier/")))
    return <><CourierNav activeTabId={path.startsWith("/courier/route") ? "route" : path.startsWith("/courier/history") ? "history" : "pool"} /><WorkspaceTopBar /></>;
  if (user.role === "specimen_collector" && (path === "/collector" || path.startsWith("/collector/")))
    return <><CollectorNav activeTabId={path.startsWith("/collector/history") ? "history" : "today"} /><WorkspaceTopBar /></>;
  return null;
}

function RoleShift({ children }) {
  const { user, ready } = useAuth();
  const location = useLocation();
  const path = location.pathname;
  const patient = ready && user?.role === "patient" && path.startsWith("/patient/");
  const guest   = ready && user?.role === "patient" && isGuestShellPath(path);
  const doctor  = ready && user?.role === "doctor"
    && (path === "/doctor" || path.startsWith("/doctor/"))
    && !/^\/doctor\/consultation\//.test(path);
  const hotel   = ready && (user?.role === "hotel_staff" || user?.role === "hotel_manager")
    && (path === "/hotel" || path.startsWith("/hotel/"));
  const admin   = ready && user?.role === "platform_admin"
    && (path === "/platform" || path.startsWith("/platform/"));
  const pharm   = ready && user?.role === "pharmacy"
    && (path === "/pharmacy" || path.startsWith("/pharmacy/") || path.startsWith("/partner/"));
  const lab     = ready && user?.role === "laboratory"
    && (path === "/laboratory" || path.startsWith("/laboratory/") || path.startsWith("/partner/"));
  const courier = ready && user?.role === "courier"
    && (path === "/courier" || path.startsWith("/courier/"));
  const collector = ready && user?.role === "specimen_collector"
    && (path === "/collector" || path.startsWith("/collector/"));
  const cls = patient
    ? "lg:pl-64 lg:pt-[4.5rem] pb-24 lg:pb-6"
    : guest
    ? "lg:pl-64 pt-12 pb-20 lg:pb-6"
    : courier || collector
    ? "lg:pl-64 pt-14 lg:pt-[4.5rem] pb-20 lg:pb-6"
    : doctor || hotel || admin || pharm || lab
    ? "lg:pl-64 pt-14 lg:pt-[4.5rem] pb-20 lg:pb-6"
    : "";
  return <div className={cls}>{children}</div>;
}

export default function App() {
  return (
    <div className="App">
      <Toaster richColors position="top-center" />
      <BrowserRouter>
        <AuthProvider>
          <UnreadProvider>
          <HotelProvider>
            <AppInner />
          </HotelProvider>
          </UnreadProvider>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

function AppInner() {
  // Route pathname is the resetKey so navigating away from a crashed
  // page automatically clears the boundary — no permanent lockout.
  const location = useLocation();
  return (
    <ErrorBoundary resetKey={location.pathname}>
      <Suspense fallback={<RouteFallback />}>
        <RoleShift>
        <Routes>
          {/* Public / onboarding */}
          <Route path="/" element={<Landing />} />
          <Route path="/start" element={<StartWizard />} />
          <Route path="/q/:token" element={<QrScan />} />
          <Route path="/reset-cache" element={<ResetCache />} />
          <Route path="/verify/:kind/:id" element={<VerifySignedDoc />} />
          <Route path="/join" element={<ParticipantJoin />} />
          <Route path="/login" element={<Login />} />
          <Route path="/login/guest" element={<GuestLogin />} />
          <Route path="/setup" element={<Setup />} />
          <Route path="/login/:role" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/auth/callback" element={<AuthCallback />} />
          <Route path="/invite/hotel/:token" element={<HotelInviteAccept />} />
          <Route path="/invite/partner/:token" element={<PartnerInviteAccept />} />
          <Route path="/compliance" element={<Compliance />} />
          <Route path="/privacy" element={<Privacy />} />

          {/* Cross-portal shared */}
          <Route path="/messages" element={<Protected roles={["patient","doctor"]}><Messages /></Protected>} />
          <Route path="/settings/consent" element={<Protected roles={["patient","doctor"]}><ConsentSettings /></Protected>} />
          <Route path="/settings/privacy" element={<Protected roles={["patient","doctor"]}><PrivacySettings /></Protected>} />
          <Route path="/security/mfa" element={<Protected roles={["doctor","hotel_manager","hotel_staff"]}><MfaSetup /></Protected>} />
          <Route path="/rollup" element={<Protected roles={["platform_admin","hotel_manager","hotel_staff","pharmacy","laboratory"]}><ChainRollup /></Protected>} />
          <Route path="/courier" element={<Protected roles={["courier"]}><CourierDashboard view="pool" /></Protected>} />
          <Route path="/courier/route" element={<Protected roles={["courier"]}><CourierDashboard view="route" /></Protected>} />
          <Route path="/courier/history" element={<Protected roles={["courier"]}><CourierDashboard view="history" /></Protected>} />
          <Route path="/collector" element={<Protected roles={["specimen_collector"]}><CollectorDashboard view="today" /></Protected>} />
          <Route path="/collector/history" element={<Protected roles={["specimen_collector"]}><CollectorDashboard view="history" /></Protected>} />
          <Route path="/partner/home" element={<Protected roles={["insurance_admin","hmo_admin","corporate_admin"]}><PartnerWorkspace /></Protected>} />

          {/* Portal-scoped route groups */}
          {PatientRoutes}
          {DoctorRoutes}
          {HotelRoutes}
          {AdminRoutes}
          {PharmacyRoutes}
          {LaboratoryRoutes}
        </Routes>
        </RoleShift>
      </Suspense>
      <RoleNav />
      {!(location.pathname === "/start" || location.pathname === "/login/guest") && <PrivacyBanner />}
      <SessionIdleWarning />
      <PropertyTheme />
      <InstallPrompt />
      <PushOptIn />
      <NetworkStatus />
    </ErrorBoundary>
  );
}
